import { Component, ErrorInfo, ReactNode } from 'react';
import { FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import i18n from '../../i18n';
import { captureException } from '../../lib/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({ error, errorInfo });

    // Reporter l'erreur à Sentry (no-op si Sentry non initialisé)
    captureException(error, { extra: { componentStack: errorInfo.componentStack } });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="max-w-2xl w-full bg-card/80 backdrop-blur-lg border border-border rounded-2xl p-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                <FiAlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">{i18n.t('errorBoundary.title', { ns: 'common' })}</h1>
                <p className="text-muted-foreground">{i18n.t('errorBoundary.description', { ns: 'common' })}</p>
              </div>
            </div>

            {this.state.error && (
              <div className="bg-background/50 border border-border rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-red-400 mb-2">{i18n.t('errorDetails', { ns: 'common' })}</h3>
                <pre className="text-xs text-foreground overflow-x-auto whitespace-pre-wrap break-words">
                  {this.state.error.toString()}
                </pre>

                {import.meta.env.DEV && this.state.errorInfo && (
                  <details className="mt-4">
                    <summary className="text-xs text-muted-foreground cursor-pointer hover:text-muted-foreground">
                      {i18n.t('errorBoundary.stackTrace', { ns: 'common' })}
                    </summary>
                    <pre className="text-xs text-muted-foreground mt-2 overflow-x-auto whitespace-pre-wrap break-words">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex space-x-4">
              <button
                onClick={this.handleReset}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-foreground rounded-lg hover:from-cyan-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2"
              >
                <FiRefreshCw className="w-5 h-5" />
                <span>{i18n.t('errorBoundary.retry', { ns: 'common' })}</span>
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex-1 px-6 py-3 bg-muted text-foreground rounded-lg hover:bg-muted transition-all"
              >
                {i18n.t('errorBoundary.reload', { ns: 'common' })}
              </button>
            </div>

            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-400 mb-2">{i18n.t('errorBoundary.suggestions', { ns: 'common' })}</h4>
              <ul className="text-sm text-foreground space-y-1">
                <li>• {i18n.t('errorBoundary.suggestion1', { ns: 'common' })}</li>
                <li>• {i18n.t('errorBoundary.suggestion2', { ns: 'common' })}</li>
                <li>• {i18n.t('errorBoundary.suggestion3', { ns: 'common' })}</li>
                <li>• {i18n.t('errorBoundary.suggestion4', { ns: 'common' })}</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
